import { useEffect, useState } from "react";
import ActionButton from "@/components/ActionButton";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatEur, formatDate, API } from "@/lib/api";
import { Plus, Upload, FileText, Loader2, CheckCircle2, X, FileSpreadsheet, Trash2 } from "lucide-react";
import HenrriJournalModal from "@/components/HenrriJournalModal";

const STATUS = { en_attente: "En attente", partiel: "Partiel", paye: "Payé", impaye: "Impayé" };
const SOURCE_LABEL = { finance: "Henrri", projet: "Projet", v2: "Devis/Fact V2", fmob: "Franchi'Mob" };
const SOURCE_STYLES = { finance: "bg-stone-100 text-stone-700", projet: "bg-blue-50 text-blue-700", v2: "bg-violet-50 text-violet-700", fmob: "bg-orange-50 text-orange-700" };

export default function Finance() {
  const [docs, setDocs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [overview, setOverview] = useState(null);
  const [impayesOnly, setImpayesOnly] = useState(false);
  const [show, setShow] = useState(false);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // {import_id, extracted, candidates}
  const [importErr, setImportErr] = useState("");
  const [showJournal, setShowJournal] = useState(false);
  const [journalResult, setJournalResult] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [savingRow, setSavingRow] = useState(null);

  const toggleStatut = async (row, paye) => {
    if (!row.doc_id) return;
    setSavingRow(row.doc_id);
    try {
      await api.patch("/finance/overview/statut", { source: row.source, doc_id: row.doc_id, paye });
      await load();
    } catch (e) {
      // no-op; laisse l'état inchangé
    } finally {
      setSavingRow(null);
    }
  };

  // Sync URL ?status= and ?focus= → set filters
  useEffect(() => {
    const s = searchParams.get("status");
    if (s === "relancer") { setStatusFilter("relancer"); setFilter("all"); }
    else if (s === "a_facturer") { setStatusFilter("a_facturer"); setFilter("all"); }
    else if (s === "paye") { setStatusFilter("paye"); setFilter("all"); }
    else if (s === "en_attente") { setStatusFilter("en_attente"); setFilter("all"); }
  }, [searchParams]);

  const load = async () => {
    const params = brandFilter === "ALL" ? {} : { brand: brandFilter };
    const [d, p, ov] = await Promise.all([api.get("/finance"), api.get("/projects"), api.get("/finance/overview", { params })]);
    setDocs(d.data); setProjects(p.data); setOverview(ov.data);
  };
  useEffect(() => { load(); }, [brandFilter]);

  const create = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    fd.montant_ht = parseFloat(fd.montant_ht || 0);
    fd.montant_ttc = parseFloat(fd.montant_ttc || fd.montant_ht * 1.2);
    fd.montant_paye = parseFloat(fd.montant_paye || 0);
    const proj = projects.find((p) => p.id === fd.project_id);
    if (proj) { fd.project_nom = proj.nom; fd.client = proj.client; fd.brand = proj.brand; }
    await api.post("/finance", fd);
    setShow(false);
    load();
  };

  const markPaid = async (id, montant_ttc) => {
    await api.put(`/finance/${id}`, { status: "paye", montant_paye: montant_ttc });
    load();
  };

  const changeStatus = async (d, status) => {
    const payload = { status };
    if (status === "paye") payload.montant_paye = d.montant_ttc;
    await api.put(`/finance/${d.id}`, payload);
    load();
  };

  const delDoc = async (id) => {
    if (!window.confirm("Supprimer ce document ? (corrige une erreur de saisie/import)")) return;
    await api.delete(`/finance/${id}`);
    load();
  };

  const onPickPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportErr("");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/import-henrri/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) {
      setImportErr(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async (final) => {
    try {
      await api.post("/finance/import-henrri/confirm", {
        import_id: preview.import_id,
        project_id: final.project_id || null,
        doc: final.doc,
      });
      setPreview(null);
      load();
    } catch (err) {
      setImportErr(err.response?.data?.detail || "Erreur de validation");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const normBrand = (b) => { const s = String(b || "").toUpperCase(); return (s.includes("MOB") || s.includes("FRANCHI")) ? "FRANCHIMOB" : "PPD"; };
  const brandDocs = brandFilter === "ALL" ? docs : docs.filter((d) => normBrand(d.brand) === brandFilter);
  let filtered = filter === "all" ? brandDocs : brandDocs.filter((d) => d.type === filter);
  if (statusFilter === "relancer") {
    filtered = filtered.filter((d) => ["facture", "acompte"].includes(d.type) && d.status !== "paye" && (d.date_emission || "9999") < today);
  } else if (statusFilter === "a_facturer") {
    filtered = filtered.filter((d) => d.type === "devis");
  } else if (statusFilter === "paye") {
    filtered = filtered.filter((d) => d.status === "paye");
  } else if (statusFilter === "en_attente") {
    filtered = filtered.filter((d) => d.status === "en_attente");
  }

  const totals = {
    facture: docs.filter((d) => ["facture", "acompte"].includes(d.type)).reduce((a, b) => a + b.montant_ttc, 0),
    encaisse: docs.reduce((a, b) => a + (b.montant_paye || 0), 0),
    attente: docs.filter((d) => d.status === "en_attente").reduce((a, b) => a + (b.montant_ttc - (b.montant_paye || 0)), 0),
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500">Finance · Vue consolidée (toutes sources)</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Devis & factures</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex bg-stone-100 rounded-full p-1" data-testid="finance-brand-toggle">
            {[["ALL", "Toutes"], ["PPD", "Pourquoi Pas Design"], ["FRANCHIMOB", "Franchi'Mob"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`finance-brand-${k.toLowerCase()}`}
                onClick={() => setBrandFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${brandFilter === k ? (k === "FRANCHIMOB" ? "bg-[var(--fmob)] text-white" : k === "PPD" ? "bg-[var(--ppd)] text-white" : "bg-stone-900 text-white") : "text-stone-500 hover:text-stone-800"}`}
              >{l}</button>
            ))}
          </div>
          <button data-testid="finance-new" onClick={() => setShow(true)} className="bg-gray-900 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Nouveau document</button>
          <label data-testid="henrri-import-button" className="bg-[#D05E44] hover:bg-[#A6452F] text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 cursor-pointer">
            {importing ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            {importing ? "Analyse PDF…" : "Importer PDF Henrri"}
            <input type="file" accept="application/pdf" onChange={onPickPdf} className="hidden" disabled={importing} data-testid="henrri-file-input"/>
          </label>
          <button data-testid="henrri-journal-button" onClick={() => setShowJournal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2">
            <FileSpreadsheet size={16}/> Import journal Henrri (XLSX)
          </button>
        </div>
      </div>

      {journalResult && (
        <div className="text-sm rounded px-3 py-2 border bg-emerald-50 border-emerald-200 text-emerald-900" data-testid="journal-result-banner">
          Import journal terminé · <span className="font-semibold">{journalResult.projects_created}</span> projet(s) créé(s) · <span className="font-semibold">{journalResult.projects_merged}</span> rattaché(s) · <span className="font-semibold">{journalResult.finance_created}</span> doc(s) · <span className="font-semibold">{journalResult.avoirs_created || 0}</span> avoir(s) · <span className="font-semibold">{journalResult.paiements_attached || 0}</span> paiement(s) · <span className="font-semibold">{journalResult.skipped}</span> ignoré(s)
        </div>
      )}

      {showJournal && (
        <HenrriJournalModal
          onClose={() => setShowJournal(false)}
          onDone={(r) => { setShowJournal(false); setJournalResult(r); load(); }}
        />
      )}

      {importErr && <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2" data-testid="henrri-error">{importErr}</div>}

      {preview && <HenrriPreview preview={preview} projects={projects} onCancel={() => setPreview(null)} onConfirm={confirmImport} />}

      {/* ===== VUE GLOBALE CONSOLIDÉE (3 sources) ===== */}
      <div data-testid="finance-overview">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ppd)] mb-2">Vue globale · toutes les factures</div>
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            <div className="card-flat p-5" data-testid="ov-ca-facture">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">CA facturé HT</div>
              <div className="kpi-number text-2xl md:text-3xl mt-2">{formatEur(overview.summary.ca_net_ht)}</div>
              <div className="text-[11px] text-stone-400 mt-1">brut {formatEur(overview.summary.ca_facture_ht)} − avoirs {formatEur(overview.summary.avoir_ht)}</div>
            </div>
            <div className="card-flat p-5" data-testid="ov-encaisse">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Encaissé HT</div>
              <div className="kpi-number text-2xl md:text-3xl mt-2 text-green-700">{formatEur(overview.summary.encaisse_ht)}</div>
            </div>
            <button onClick={() => setImpayesOnly((v) => !v)} data-testid="ov-impayes" className={`card-flat p-5 text-left transition ${impayesOnly ? "ring-2 ring-rose-400" : "hover:ring-1 hover:ring-rose-200"}`}>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Impayés · reste dû HT</div>
              <div className="kpi-number text-2xl md:text-3xl mt-2 text-rose-700">{formatEur(overview.summary.impaye_ht)}</div>
              <div className="text-[11px] text-stone-400 mt-1">{overview.summary.nb_impayees} facture(s) · filtrer</div>
            </button>
            <div className="card-flat p-5" data-testid="ov-retard">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Dont en retard HT</div>
              <div className="kpi-number text-2xl md:text-3xl mt-2 text-amber-700">{formatEur(overview.summary.en_retard_ht)}</div>
              <div className="text-[11px] text-stone-400 mt-1">échéance dépassée</div>
            </div>
          </div>
        )}

        {overview && (
          <div className="card-flat overflow-x-auto -mx-4 md:mx-0 mt-4">
            <div className="flex items-center justify-between px-4 pt-3 flex-wrap gap-2">
              <div className="text-xs text-stone-500 uppercase tracking-widest font-semibold">
                {impayesOnly ? "Factures impayées uniquement" : "Toutes les factures"} · {overview.factures.filter((r) => !impayesOnly || r.reste_ht > 0.01).length}
              </div>
              <button data-testid="ov-toggle-impayes" onClick={() => setImpayesOnly((v) => !v)} className={`px-3 py-1 text-xs font-semibold rounded border ${impayesOnly ? "bg-rose-600 text-white border-rose-600" : "bg-white border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                {impayesOnly ? "Voir tout" : "Impayés seulement"}
              </button>
            </div>
            <table className="w-full text-sm min-w-[820px] mt-2">
              <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500 border-b">
                <tr>
                  <th className="px-4 py-3 text-center" title="Marquer payé / impayé">Payé</th>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-left">N°</th>
                  <th className="px-4 py-3 text-left">Projet / Client</th>
                  <th className="px-4 py-3 text-left">Émission</th>
                  <th className="px-4 py-3 text-right">HT</th>
                  <th className="px-4 py-3 text-right">Payé HT</th>
                  <th className="px-4 py-3 text-right">Reste HT</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {overview.factures.filter((r) => !impayesOnly || r.reste_ht > 0.01).map((r, i) => (
                  <tr key={`${r.source}-${r.numero}-${i}`} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`ov-row-${i}`}>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        data-testid={`ov-paye-${i}`}
                        checked={r.reste_ht <= 0.01}
                        disabled={savingRow === r.doc_id}
                        onChange={(e) => toggleStatut(r, e.target.checked)}
                        title={r.reste_ht <= 0.01 ? "Marquer impayé" : "Marquer payé"}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3"><span className={`pill text-[10px] ${SOURCE_STYLES[r.source] || "bg-stone-100 text-stone-600"}`}>{SOURCE_LABEL[r.source] || r.source}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{r.numero}</td>
                    <td className="px-4 py-3">
                      {r.project_id ? (
                        <button onClick={() => navigate(`/projets/${r.project_id}`)} data-testid={`ov-link-${i}`} className="text-[var(--ppd)] font-medium hover:underline text-left">
                          {r.project_nom} ↗
                        </button>
                      ) : (
                        <span className="text-stone-600">{r.project_nom}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatDate(r.date_emission)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{formatEur(r.montant_ht)}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-700">{formatEur(r.paye_ht)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${r.reste_ht > 0.01 ? "text-rose-700" : "text-stone-300"}`}>{formatEur(r.reste_ht)}</td>
                    <td className="px-4 py-3">
                      <span className={`pill text-[10px] ${r.statut === "payee" ? "bg-emerald-50 text-emerald-700" : r.statut === "partiel" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                        {r.statut === "payee" ? "Payé" : r.statut === "partiel" ? "Partiel" : "Impayé"}
                      </span>
                      {r.en_retard && <span className="pill text-[10px] bg-rose-100 text-rose-800 ml-1">en retard</span>}
                    </td>
                  </tr>
                ))}
                {overview.factures.filter((r) => !impayesOnly || r.reste_ht > 0.01).length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">{impayesOnly ? "Aucune facture impayée 🎉" : "Aucune facture."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {overview && overview.projets_a_completer && overview.projets_a_completer.length > 0 && (
        <div className="card-flat p-4 border-l-4 border-amber-400 bg-amber-50/40" data-testid="finance-a-completer">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-2">
            Projets à compléter · {overview.projets_a_completer.length} — montant facturé manquant
          </div>
          <div className="space-y-1.5">
            {overview.projets_a_completer.map((p, i) => (
              <div key={`ac-${i}`} data-testid={`ac-row-${i}`} className="flex items-center justify-between text-sm bg-white rounded border border-amber-200 px-3 py-2">
                <div>
                  <span className="font-semibold">{p.project_nom || "Projet sans nom"}</span>
                  <span className="text-stone-500"> · </span>
                  <span className="font-mono text-xs text-stone-600">{p.numero}</span>
                  <span className="pill text-[10px] ml-2 bg-stone-100 text-stone-600">{SOURCE_LABEL[p.source] || p.source}</span>
                </div>
                {p.project_id && (
                  <a href={`/projets/${p.project_id}`} className="text-xs text-[var(--ppd)] font-semibold hover:underline" data-testid={`ac-link-${i}`}>Compléter →</a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 pt-2">Détail · saisie manuelle Henrri</div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {["all", "devis", "acompte", "facture", "avoir"].map((f) => (
            <button key={f} data-testid={`finance-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-xs font-semibold rounded ${filter === f ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>{f === "all" ? "Tous" : f}</button>
          ))}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[
            ["all", "Tous statuts"],
            ["en_attente", "En attente"],
            ["relancer", "À relancer"],
            ["a_facturer", "Devis à facturer"],
            ["paye", "Payés"],
          ].map(([id, label]) => (
            <button key={id} data-testid={`finance-status-${id}`} onClick={() => setStatusFilter(id)} className={`px-3 py-1.5 text-xs font-semibold rounded border ${statusFilter === id ? "bg-[var(--ppd)] text-white border-[var(--ppd)]" : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="text-xs text-stone-500 uppercase tracking-widest font-semibold mb-1 flex items-center gap-2" data-testid="finance-ht-banner">
        <span>Montants affichés : <span className="text-stone-900">HT</span></span>
        <span className="text-stone-400">·</span>
        <span className="text-stone-500">TVA & TTC en information secondaire</span>
      </div>

      <div className="card-flat overflow-x-auto -mx-4 md:mx-0">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500 border-b">
            <tr>
              <th className="px-4 py-3 text-left">N°</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Client/Projet</th>
              <th className="px-4 py-3 text-left">Émission</th>
              <th className="px-4 py-3 text-right">HT</th>
              <th className="px-4 py-3 text-right text-stone-400">TVA</th>
              <th className="px-4 py-3 text-right text-stone-400">TTC</th>
              <th className="px-4 py-3 text-right">Payé HT</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-4 py-3 font-mono text-xs">{d.numero}</td>
                <td className="px-4 py-3"><span className="pill bg-stone-100 text-stone-700">{d.type}</span></td>
                <td className="px-4 py-3"><div>{d.client}</div><div className="text-xs text-stone-500">{d.project_nom}</div></td>
                <td className="px-4 py-3">{formatDate(d.date_emission)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{formatEur(d.montant_ht)}</td>
                <td className="px-4 py-3 text-right font-mono text-stone-400 text-xs">{formatEur(d.tva || (d.montant_ttc - d.montant_ht))}</td>
                <td className="px-4 py-3 text-right font-mono text-stone-400 text-xs">{formatEur(d.montant_ttc)}</td>
                <td className="px-4 py-3 text-right font-mono">{formatEur(d.montant_paye_ht || 0)}</td>
                <td className="px-4 py-3">
                  <select value={d.status} onChange={(e) => changeStatus(d, e.target.value)} data-testid={`finance-status-select-${d.id}`} className={`text-xs font-semibold rounded px-2 py-1 border cursor-pointer ${d.status === "paye" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : d.status === "impaye" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {Object.entries(STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`finance-del-${d.id}`} onClick={() => delDoc(d.id)} className="text-stone-400 hover:text-rose-600" title="Supprimer ce document"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-lg p-6 max-w-lg w-full" data-testid="finance-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouveau document</h3>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <select name="type" required className="border border-gray-300 rounded px-3 py-2"><option value="devis">Devis</option><option value="acompte">Acompte</option><option value="facture">Facture</option><option value="avoir">Avoir</option></select>
                <input name="numero" required placeholder="N° doc" className="border border-gray-300 rounded px-3 py-2"/>
              </div>
              <select name="project_id" className="w-full border border-gray-300 rounded px-3 py-2">
                <option value="">— Projet —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input name="date_emission" type="date" required className="border border-gray-300 rounded px-3 py-2"/>
                <input name="date_echeance" type="date" className="border border-gray-300 rounded px-3 py-2"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input name="montant_ht" type="number" placeholder="HT" className="border border-gray-300 rounded px-3 py-2"/>
                <input name="montant_ttc" type="number" placeholder="TTC" className="border border-gray-300 rounded px-3 py-2"/>
              </div>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border rounded">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="finance-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-gray-900 text-white rounded font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function HenrriPreview({ preview, projects, onCancel, onConfirm }) {
  const ex = preview.extracted || {};
  const [projectId, setProjectId] = useState(preview.candidates?.[0]?.project_id || "");
  const [doc, setDoc] = useState({
    type: ex.type || "facture",
    numero: ex.numero || "",
    date_emission: ex.date_emission || "",
    date_echeance: ex.date_echeance || "",
    client: ex.client || "",
    montant_ht: ex.montant_ht || 0,
    montant_ttc: ex.montant_ttc || 0,
    status: ex.statut || "en_attente",
  });
  const update = (k, v) => setDoc((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="henrri-preview-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><FileText size={20}/> Contrôle avant validation</h3>
          <button onClick={onCancel}><X size={18}/></button>
        </div>
        <p className="text-sm text-gray-500 mb-5">Vérifiez et corrigez les données extraites par l'IA avant import définitif.</p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Type</span>
            <select value={doc.type} onChange={(e) => update("type", e.target.value)} data-testid="prev-type" className="border border-gray-300 rounded px-3 py-2 mt-1">
              <option value="devis">Devis</option><option value="acompte">Facture acompte</option><option value="facture">Facture</option><option value="avoir">Avoir</option>
            </select>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Numéro</span>
            <input value={doc.numero} onChange={(e) => update("numero", e.target.value)} data-testid="prev-numero" className="border border-gray-300 rounded px-3 py-2 mt-1 font-mono"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Date émission</span>
            <input type="date" value={doc.date_emission} onChange={(e) => update("date_emission", e.target.value)} data-testid="prev-date" className="border border-gray-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Échéance</span>
            <input type="date" value={doc.date_echeance} onChange={(e) => update("date_echeance", e.target.value)} className="border border-gray-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Client</span>
            <input value={doc.client} onChange={(e) => update("client", e.target.value)} data-testid="prev-client" className="border border-gray-300 rounded px-3 py-2 mt-1"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Montant HT (€)</span>
            <input type="number" step="0.01" value={doc.montant_ht} onChange={(e) => update("montant_ht", parseFloat(e.target.value) || 0)} data-testid="prev-ht" className="border border-gray-300 rounded px-3 py-2 mt-1 font-mono"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Montant TTC (€)</span>
            <input type="number" step="0.01" value={doc.montant_ttc} onChange={(e) => update("montant_ttc", parseFloat(e.target.value) || 0)} data-testid="prev-ttc" className="border border-gray-300 rounded px-3 py-2 mt-1 font-mono"/>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Statut</span>
            <select value={doc.status} onChange={(e) => update("status", e.target.value)} data-testid="prev-status" className="border border-gray-300 rounded px-3 py-2 mt-1">
              <option value="en_attente">En attente</option><option value="partiel">Partiel</option><option value="paye">Payé</option><option value="impaye">Impayé</option>
            </select>
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Projet à rattacher</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="prev-project" className="border border-gray-300 rounded px-3 py-2 mt-1">
              <option value="">— Aucun (créer hors projet) —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.nom} · {p.client}</option>)}
            </select>
          </label>
        </div>

        {preview.candidates?.length > 0 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-1.5">Projets suggérés par l'IA</div>
            <div className="flex flex-wrap gap-1.5">
              {preview.candidates.map((c) => (
                <button
                  key={c.project_id}
                  type="button"
                  data-testid={`prev-cand-${c.project_id}`}
                  onClick={() => setProjectId(c.project_id)}
                  className={`text-xs px-2.5 py-1 rounded border ${projectId === c.project_id ? "bg-amber-800 text-white border-amber-800" : "bg-white border-amber-300 hover:bg-amber-100"}`}
                >
                  {c.nom} · {c.client} <span className="opacity-60">({c.score})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {ex.lignes?.length > 0 && (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-gray-600 font-semibold">Voir les {ex.lignes.length} lignes détectées</summary>
            <ul className="mt-2 space-y-1">
              {ex.lignes.map((l, i) => (
                <li key={i} className="font-mono text-[11px] text-gray-700">{l.description} — {l.quantite} {l.unite} × {l.prix_unitaire} = {l.montant_ht} €</li>
              ))}
            </ul>
          </details>
        )}

        <div className="flex gap-2 mt-6 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded">Annuler</button>
          <button
            data-testid="prev-confirm"
            onClick={() => onConfirm({ project_id: projectId, doc })}
            className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold flex items-center gap-2"
          >
            <CheckCircle2 size={14}/> Valider l'import
          </button>
        </div>
      </div>
    </div>
  );
}
